import React, { useState, useEffect, useRef } from "react";
import { CSVLink } from "react-csv";
import { ButtonOutline } from "../styledConstants";
import IconDownload from "../../assets/images/IconDownload";
import ApiRequest from "../../utils/ApiRequest";

/**
 * Props:
 *  - digit, headers, url, params, type, title, contentType, reportName, method
 *  - fetcher: optional async function(params) that returns the API response (preferred)
 *
 * If fetcher is provided, CsvDownApi will call fetcher(params) and parse the response the same way
 * as before. Otherwise it will use ApiRequest with `url` as earlier.
 */
const CsvDownApi = ({
  digit = 0,
  headers,
  url,
  params = {},
  type = "",
  title = "DOWNLOAD CSV.",
  contentType,
  reportName = "Report.csv",
  method = "post",
  fetcher, // <-- new: optional async function to call API (should return same shape as ApiRequest)
}) => {
  const [data, setData] = useState([]);
  const [isDisabled, setIsDisabled] = useState(false);
  const [triggerDownload, setTriggerDownload] = useState(false);
  const csvLinkRef = useRef(null);

  useEffect(() => {
    if (triggerDownload && data.length > 0 && csvLinkRef.current) {
      // trigger anchor click
      csvLinkRef.current.link.click();
      setTriggerDownload(false);
    }
  }, [data, triggerDownload]);

  const downloadReport = () => {
    if (isDisabled) return;
    setIsDisabled(true);
    getUserList();

    // keep button disabled for short time to avoid repeated clicks
    setTimeout(() => {
      setIsDisabled(false);
    }, 15000);
  };

  const parseResponseToArray = (res) => {
    let contentData = [];

    try {
      if (Array.isArray(res?.data?.metaInfo?.data)) {
        contentData = res.data.metaInfo.data.map((row) => {
          const v = row.vpa || {};
          return {
            ...row,
            id: v.id ?? "",
            vpa: v.vpa ?? "",
            handle: v.handle ?? "",
            status: v.status ?? "",
            isMultipleVpa: v.isMultipleVpa ?? "",
            createdAt: v.createdAt ?? "",
            updatedAt: v.updatedAt ?? "",
            allocatedAt: v.allocatedAt ?? "",
            orderId: v.orderId ?? "",
          };
        });
      } else if (Array.isArray(res?.data?.data?.data)) {
        contentData = res.data.data.data;
      } else if (Array.isArray(res?.data)) {
        contentData = res.data;
      } else if (Array.isArray(res?.data?.data?.content)) {
        contentData = res.data.data.content;
      } else if (Array.isArray(res?.data?.data?.transactions)) {
        contentData = res?.data?.data?.transactions;
      } else if (Array.isArray(res)) {
        contentData = res;
      }
    } catch (err) {
      console.error("CSV Download parsing error:", err);
    }

    if (digit === 1) {
      contentData = contentData.map((value) => ({
        ...value,
        transactionId: "txn_" + value.transactionId,
      }));
    }

    return contentData;
  };

  const getUserList = async () => {
    if (typeof fetcher === "function") {
      // Use provided fetcher (e.g. getInventoryList)
      try {
        const res = await fetcher(params);
        const contentData = parseResponseToArray(res);
        setData(contentData);
        setTriggerDownload(true);
      } catch (err) {
        console.error("CSV Download fetcher error:", err);
      }
      return;
    }

    // fallback to ApiRequest as original
    const successHandler = (res) => {
      const contentData = parseResponseToArray(res);
      setData(contentData);
      setTriggerDownload(true);
    };

    const errorHandler = (err) => {
      console.error("CSV Download API Error:", err);
    };

    const api = new ApiRequest("", successHandler, errorHandler, false);
    if (method === "post") {
      api.post(url, params);
    } else {
      api.get(url, params);
    }
  };

  return (
    <>
      {data.length > 0 && (
        <CSVLink className="hidden" data={data} headers={headers} filename={reportName} ref={csvLinkRef}>
          {""}
        </CSVLink>
      )}

      {url || typeof fetcher === "function" ? (
        <ButtonOutline
          as="span"
          className="flex flex-center"
          primary
          rg
          onClick={downloadReport}
          disabled={isDisabled}
          style={{
            cursor: isDisabled ? "not-allowed" : "pointer",
            opacity: isDisabled ? 0.6 : 1,
            border: "1px solid #d3d3d3",
          }}
        >
          <IconDownload className="mr8" />
          {isDisabled ? "Please wait..." : title}
        </ButtonOutline>
      ) : (
        <ButtonOutline as="span" className="flex flex-center" disabled lg>
          <IconDownload className="mr8" /> {title}
        </ButtonOutline>
      )}
    </>
  );
};

export default CsvDownApi;
